/**
 * Interactive Table Management Modal
 * Click on tables to confirm, edit names, set capacity
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { api } from '../api/client';

interface TableData {
  id?: number;
  tableNumber: number;
  displayName: string;
  capacity: number;
  boundingBox: { x1: number; y1: number; x2: number; y2: number };
  position: { x: number; y: number };
  confirmed?: boolean;
}

interface Props {
  isOpen: boolean;
  table: TableData | null;
  onClose: () => void;
  onSave: (table: TableData) => void;
}

const Overlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  justify-content: center;
  align-items: center;
  z-index: 10000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

const Header = styled.h2`
  margin: 0 0 24px 0;
  font-size: 24px;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #2196f3;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' && `
    background: #2196f3;
    color: white;

    &:hover {
      background: #1976d2;
    }
  `}

  ${props => props.variant === 'secondary' && `
    background: #e0e0e0;
    color: #333;

    &:hover {
      background: #d0d0d0;
    }
  `}

  ${props => props.variant === 'danger' && `
    background: #f44336;
    color: white;

    &:hover {
      background: #d32f2f;
    }
  `}
`;

const InfoText = styled.p`
  font-size: 14px;
  color: #666;
  margin: 16px 0;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 8px;
`;

const StatusBadge = styled.span<{ confirmed?: boolean }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.confirmed ? '#4caf50' : '#ff9800'};
  color: white;
`;

const TableManagementModal: React.FC<Props> = ({ isOpen, table, onClose, onSave }) => {
  const [displayName, setDisplayName] = useState(table?.displayName || '');
  const [capacity, setCapacity] = useState(table?.capacity || 4);
  const [saving, setSaving] = useState(false);

  // Update state when table prop changes
  React.useEffect(() => {
    if (table) {
      setDisplayName(table.displayName || `Table ${table.tableNumber}`);
      setCapacity(table.capacity || 4);
    }
  }, [table]);

  const handleSave = async () => {
    if (!table) return;

    setSaving(true);
    try {
      if (table.id) {
        // Edit existing confirmed table
        await api.put(`/table-management/edit/${table.id}`, {
          tableNumber: table.tableNumber,
          displayName,
          capacity,
          userId: 'user'
        });
      } else {
        // Confirm new table
        await api.post('/table-management/confirm', {
          locationId: 1,
          tableNumber: table.tableNumber,
          displayName,
          boundingBox: table.boundingBox,
          position: table.position,
          capacity,
          confirmedBy: 'user'
        });
      }

      onSave({
        ...table,
        displayName,
        capacity,
        confirmed: true
      });

      onClose();
    } catch (error) {
      console.error('Failed to save table:', error);
      alert('Failed to save table. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!table) return null;

  return (
    <Overlay isOpen={isOpen} onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          🍽️ Table Management
          <StatusBadge confirmed={table.confirmed}>
            {table.confirmed ? 'Confirmed' : 'Not Confirmed'}
          </StatusBadge>
        </Header>

        <InfoText>
          📍 Position: ({Math.round(table.position.x)}, {Math.round(table.position.y)})
          <br />
          🔢 Detected Table Number: <strong>#{table.tableNumber}</strong>
        </InfoText>

        <FormGroup>
          <Label htmlFor="displayName">Display Name</Label>
          <Input
            id="displayName"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., Table 1, Window Table, Corner Booth"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="capacity">Seating Capacity</Label>
          <Input
            id="capacity"
            type="number"
            min="1"
            max="20"
            value={capacity}
            onChange={(e) => setCapacity(parseInt(e.target.value) || 4)}
          />
        </FormGroup>

        <ButtonRow>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : table.confirmed ? 'Update' : 'Confirm Table'}
          </Button>
        </ButtonRow>

        {table.confirmed && (
          <InfoText style={{ marginTop: 16, background: '#e8f5e9', color: '#2e7d32' }}>
            ✅ This table is confirmed and saved in the database.
          </InfoText>
        )}
      </Modal>
    </Overlay>
  );
};

export default TableManagementModal;



